
import React, { useEffect, useRef, useState } from 'react';
import { Eye, Upload, FileText, User, Calendar, DollarSign, Percent, Clock, Save, Building2, CreditCard, Shield, Banknote, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { CalculationInputs, RentalScheduleItem, CalculationSummary } from '../../utils/calculationUtils';
import apiService from '../../services/apiService';
import TermsheetDrawer from './TermsheetDrawer';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'react-toastify';
import { cn } from '../../lib/utils';

interface ApiResponse<T> {
  data: T;
  downloaded: T;
  message?: string;
  status?: number;
}

interface SavedCalculation {
  id: string;
  name: string;
  timestamp: Date;
  inputs: CalculationInputs;
  schedule: RentalScheduleItem[];
  summary: CalculationSummary;
  termSheetGenerated?: boolean;
  createdBy?: string;
  calculationId?: string;
  termSheetId?: string;
  remarks?: string;
  downloadedAt?: string;
}

function hasTermSheetId(c: SavedCalculation): boolean {
  return c.termSheetId != null && String(c.termSheetId).trim() !== "";
}

interface SavedCalculationsProps {
  calculations: SavedCalculation[];
  onLoad: (calculation: SavedCalculation) => void;
  onTermSheetDownload: (calculation: SavedCalculation) => void;
  onUpdateCalculation?: (calculation: SavedCalculation) => void;
  /** Called after successful delete so parent can drop the row from state */
  onTermSheetDeleted?: (calculationId: string) => void;
  customerId?: string;
}

const SavedCalculations: React.FC<SavedCalculationsProps> = ({
  calculations,
  onLoad,
  onTermSheetDownload,
  onUpdateCalculation,
  onTermSheetDeleted,
  customerId,
}) => {
  const [selectedCalculation, setSelectedCalculation] = useState<SavedCalculation | null>(null);
  const [remark, setRemark] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyCity, setCompanyCity] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SavedCalculation | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const getTermSheetApiId = (c: SavedCalculation) =>
    (c.termSheetId && String(c.termSheetId).trim()) || c.id;
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatLabelRentalCalculationBasis = (key: string) => {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space before capital letters
      .replace(/^./, (s) => s.toUpperCase()) // Capitalize first letter
      .replace(/\bGst\b/, 'GST') // Optional: force uppercase GST
      .replace(/\bTds\b/, 'TDS'); // Optional: force uppercase TDS
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  useEffect(() => {
    const storedCustomer = sessionStorage.getItem("selectedCustomer");
          if (storedCustomer) {
              const parsed = JSON.parse(storedCustomer);
              setCompanyName(parsed.companyName || parsed.name);
              setCompanyCity(parsed.city || null); 
          }
        }, []);


  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleRemarkSave = (remark: string) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        await apiService.post<ApiResponse<any>>(
          `/customers/update-term-sheet-remark/${selectedCalculation?.id}`,
          { remark }
        );

        if (selectedCalculation && onUpdateCalculation) {
          const updatedCalculation = { ...selectedCalculation, remarks: remark };
          onUpdateCalculation(updatedCalculation);
          setSelectedCalculation(updatedCalculation);
        }

      } catch (error) {
        console.error("Failed to update remark:", error);
      }
    }, 800); // adjust debounce delay as needed
  };

  const formatCompactCurrency = (value: number) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  const handleTermSheetGenerate = (calculation: SavedCalculation) => {
    setDrawerOpen(true);
    onTermSheetDownload(calculation);
    // Update the calculation to mark term sheet as generated


    if (selectedCalculation && onUpdateCalculation) {
      const updatedCalculation = { ...selectedCalculation, remarks: remark };
      onUpdateCalculation(updatedCalculation);
      setSelectedCalculation(updatedCalculation);
    }
  };

  // const handleSaveRemark = () => {
  //   if (selectedCalculation && onUpdateCalculation) {
  //     const updatedCalculation = { ...selectedCalculation, remark };
  //     onUpdateCalculation(updatedCalculation);
  //     setSelectedCalculation(updatedCalculation);
  //     toast({
  //       title: "Remark Saved",
  //       description: "Your remark has been saved successfully.",
  //     });
  //   }
  // };

  const handleViewClick = (calculation: SavedCalculation) => {
    setSelectedCalculation(calculation);
    setRemark(calculation.remarks || '');
  };

  const confirmDeleteTermSheet = async () => {
    if (!deleteTarget) return;
    if (hasTermSheetId(deleteTarget)) {
      toast.error('Cannot delete after a term sheet has been generated');
      setDeleteTarget(null);
      return;
    }
    const termId = getTermSheetApiId(deleteTarget);
    try {
      setDeleteSubmitting(true);
      await apiService.post(`/customers/delete-term-sheet/${termId}`, {});
      toast.success('Term sheet deleted');
      onTermSheetDeleted?.(deleteTarget.id);
      if (selectedCalculation?.id === deleteTarget.id) {
        setSelectedCalculation(null);
        setDrawerOpen(false);
      }
      setDeleteTarget(null);
    } catch (error) {
      console.error('Failed to delete term sheet:', error);
      toast.error('Failed to delete term sheet');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (calculations.length === 0) {
    return (
      <Card className="h-96">
        <CardContent className="h-full flex items-center justify-center">
          <div className="text-center p-8">
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Saved Calculations</h3>
            <p className="text-gray-500 max-w-md">Complete and save calculations to view them here.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Saved Calculations</h2>
          <p className="text-gray-600 mt-1">Manage and review your rental calculations</p>
        </div>
        <div className="text-sm text-gray-600">
          {calculations.length} {calculations.length === 1 ? 'Calculation' : 'Calculations'}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">Calculation History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b">
                  <TableHead className="font-semibold text-gray-700">Calculation ID</TableHead>
                  <TableHead className="font-semibold text-gray-700">Created by</TableHead>
                  <TableHead className="font-semibold text-gray-700">Created at</TableHead>
                  <TableHead className="font-semibold text-gray-700">Asset Value</TableHead>
                  <TableHead className="font-semibold text-gray-700">Disbursement Amount</TableHead>
                  <TableHead className="font-semibold text-gray-700">Frequency</TableHead>
                  <TableHead className="font-semibold text-gray-700">Tenure</TableHead>
                  <TableHead className="font-semibold text-gray-700">PTPM</TableHead>
                  <TableHead className="font-semibold text-gray-700">Margin</TableHead>
                  <TableHead className="font-semibold text-gray-700">Discounting Factor</TableHead>
                  <TableHead className="font-semibold text-gray-700">GST Passback</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calculations.map((calculation) => (
                  <TableRow key={calculation.id} className="hover:bg-gray-50">
                    <TableCell className="font-mono text-sm font-medium">
                      {calculation.calculationId}
                    </TableCell>
                    <TableCell className="text-sm">{calculation.createdBy}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(calculation.timestamp)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatCompactCurrency(calculation.inputs.totalRental)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {calculation.inputs.disbursementAmount || 0}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{calculation.inputs.rentalFrequency === "quarterly" ? "Quarterly" : "Monthly"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{calculation.inputs.totalMonths}M</Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {calculation?.inputs?.finalPTPM}
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium ${calculation.summary.isNegativeMargin ? 'text-red-600' : 'text-green-600'
                        }`}>
                        {Number(calculation.summary.marginPercentage).toFixed(2)}%
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {Number(calculation.inputs.discountingFactor * 100).toFixed(2)}%
                    </TableCell>
                    <TableCell>
                      <Badge variant={calculation.inputs.gstPassbackEnabled ? "default" : "secondary"}>
                        {calculation.inputs.gstPassbackEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewClick(calculation)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </SheetTrigger>
                          <SheetContent className="w-[600px] sm:w-[800px] overflow-y-auto">
                            <SheetHeader className="pb-6">
                              <SheetTitle className="flex items-center gap-3 text-xl">
                                <div className="p-2 bg-[#f8f6f1] rounded-lg">
                                  <FileText className="h-6 w-6 text-[#9e7926]" />
                                </div>
                                <div>
                                  <div className="text-xl font-bold text-gray-900">Calculation Details</div>
                                  <div className="text-sm text-gray-500 font-mono">{selectedCalculation ? selectedCalculation.calculationId : ''}</div>
                                </div>
                              </SheetTitle>
                            </SheetHeader>
                            {selectedCalculation && (
                              <div className="space-y-8">
                                {/* Basic Information */}
                                <div className="bg-[#f8f6f1] rounded-lg p-6">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <User className="h-5 w-5 text-[#9e7926]" />
                                    Basic Information
                                  </h3>
                                  <div className="grid grid-cols-2 gap-6">
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <FileText className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Calculation ID</Label>
                                        <div className="font-mono font-semibold text-gray-900">{selectedCalculation.calculationId}</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <User className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Created by</Label>
                                        <div className="font-semibold text-gray-900">{selectedCalculation.createdBy}</div>
                                      </div>
                                    </div>
                                    <div className="col-span-2 flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Calendar className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Created at</Label>
                                        <div className="font-semibold text-gray-900">{formatDate(selectedCalculation.timestamp)}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Asset & GST Information */}
                                <div className="bg-[#f8f6f1] rounded-lg p-6">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <Building2 className="h-5 w-5 text-[#9e7926]" />
                                    Asset & Tax Information
                                  </h3>
                                  <div className="grid grid-cols-2 gap-6">
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <DollarSign className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Asset Value</Label>
                                        <div className="font-semibold text-gray-900">{formatCurrency(selectedCalculation.inputs.totalRental)}</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Percent className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">GST Rate</Label>
                                        <div className="font-semibold text-gray-900">{(selectedCalculation.inputs.gstRate * 100).toFixed(0)}%</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Rental Terms */}
                                <div className="bg-[#f8f6f1] rounded-lg p-6">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-[#9e7926]" />
                                    Rental Terms
                                  </h3>
                                  <div className="grid grid-cols-2 gap-6">
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Clock className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Frequency</Label>
                                        <div className="font-semibold text-gray-900 capitalize">{selectedCalculation.inputs.rentalFrequency}</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Calendar className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Tenure</Label>
                                        <div className="font-semibold text-gray-900">{selectedCalculation.inputs.totalMonths} Months</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <DollarSign className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">PTPM Consideration</Label>
                                          <div className="font-semibold text-gray-900">{selectedCalculation?.inputs?.ptpmConsideration}</div>
                                      </div>

                                    </div>

                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <DollarSign className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Final PTPM</Label>
                                        <div className="font-semibold text-gray-900">{selectedCalculation?.inputs?.finalPTPM}</div>
                                      </div>
                                      
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Percent className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">GST Passback</Label>
                                        <Badge variant={selectedCalculation.inputs.gstPassbackEnabled ? "default" : "secondary"} className="ml-2">
                                          {selectedCalculation.inputs.gstPassbackEnabled ? "Enabled" : "Disabled"}
                                        </Badge>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Percent className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Discounting Factor</Label>
                                        <div className="font-semibold text-gray-900">{Number(selectedCalculation.inputs.discountingFactor * 100).toFixed(2)}%</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Clock className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Rental Calculation Basis</Label>
                                        <div className="font-semibold text-gray-900 capitalize">{formatLabelRentalCalculationBasis(selectedCalculation.inputs.rentalCalculationBasis)}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Financial Details */}
                                <div className="bg-[#f8f6f1] rounded-lg p-6">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <CreditCard className="h-5 w-5 text-[#9e7926]" />
                                    Financial Details
                                  </h3>
                                  <div className="grid grid-cols-2 gap-6">
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Shield className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Security Deposit</Label>
                                        <div className="font-semibold text-gray-900">{formatCurrency(selectedCalculation?.summary?.securityDeposit)}  ({selectedCalculation?.inputs?.securityDepositPercentage}%) </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <CreditCard className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Processing Fee</Label>
                                        <div className="font-semibold text-gray-900">{formatCurrency(selectedCalculation?.summary?.processingFee)}   ({selectedCalculation?.inputs?.processingFeePercentage}%) </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Banknote className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Residual Value</Label>
                                        <div className="font-semibold text-gray-900">{formatCurrency(selectedCalculation?.inputs?.refundableSecurity || 0)}    ({selectedCalculation?.inputs?.refundableSecurityPercentage}%) </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Summary Information */}
                                <div className="bg-[#f8f6f1] rounded-lg p-6">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-[#9e7926]" />
                                    Summary Information
                                  </h3>
                                  <div className="grid grid-cols-2 gap-6">
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <DollarSign className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Disbursement Amount</Label>
                                        <div className="font-semibold text-gray-900">{formatCurrency(selectedCalculation.inputs.disbursementAmount || 0)}</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <DollarSign className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Margin</Label>
                                        <div className={`font-semibold ${selectedCalculation.summary.isNegativeMargin ? 'text-red-600' : 'text-green-600'}`}>
                                          {formatCurrency(selectedCalculation.summary.marginAvailable)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-span-2 flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Percent className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Margin Percentage</Label>
                                        <div className={`font-semibold ${selectedCalculation.summary.isNegativeMargin ? 'text-red-600' : 'text-green-600'}`}>
                                          {Number(selectedCalculation.summary.marginPercentage).toFixed(2)}%
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <Separator />

                                {/* Remark Section */}
                                <div className="bg-gray-50 rounded-lg p-6">
                                  <div className="flex items-center gap-2 mb-4">
                                    <FileText className="h-5 w-5 text-gray-600" />
                                    <Label className="text-lg font-semibold text-gray-900">Remark</Label>
                                  </div>
                                  <div className="flex gap-3">


                                    <Textarea
                                      value={remark}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setRemark(value); // ✅ Update local state so it shows what you type
                                        handleRemarkSave(value); // ✅ Save with debounce
                                      }}
                                      placeholder="Add your remark here..."
                                      className="flex-1 h-[100px]"
                                    />
                                    {/* <Button onClick={handleSaveRemark} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
                                      <Save className="h-4 w-4" />
                                      Save
                                    </Button> */}
                                  </div>
                                </div>
                              </div>
                            )}
                          </SheetContent>
                        </Sheet>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onLoad(calculation)}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>

                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedCalculation(calculation);
                            setDrawerOpen(true);
                          }}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={hasTermSheetId(calculation)}
                          className={cn(
                            "text-red-600 hover:text-red-700 hover:bg-red-50",
                            hasTermSheetId(calculation) &&
                              "text-slate-300 hover:bg-transparent hover:text-slate-300"
                          )}
                          title={
                            hasTermSheetId(calculation)
                              ? "Cannot delete after a term sheet has been generated"
                              : "Delete saved calculation"
                          }
                          onClick={() => setDeleteTarget(calculation)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                               
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      {drawerOpen && selectedCalculation && (
        <TermsheetDrawer
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedCalculation(null);
          }}
          selectedCalculation={selectedCalculation}
          companyName={companyName || ""}
          companyCity={companyCity || ""}
          customerId={customerId}
          onSaveSuccess={(updated) => {
            const next = { ...selectedCalculation, ...updated };
            setSelectedCalculation(next as SavedCalculation);
            onUpdateCalculation?.(next as SavedCalculation);
          }}
          termSheetId={selectedCalculation.termSheetId ?? ""}
        />
      )}
    </div>

    <AlertDialog
      open={!!deleteTarget}
      onOpenChange={(open) => {
        if (!open && !deleteSubmitting) setDeleteTarget(null);
      }}
    >
      <AlertDialogContent className="bg-white">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this term sheet?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the saved calculation from your history. You cannot undo this action.
            {deleteTarget?.calculationId != null && (
              <span className="mt-2 block font-mono text-xs text-slate-600">
                {deleteTarget.calculationId}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteSubmitting}>Cancel</AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            className="border-transparent bg-red-600 text-white hover:bg-red-700 hover:text-white focus-visible:ring-red-600"
            disabled={deleteSubmitting}
            onClick={() => void confirmDeleteTermSheet()}
          >
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default SavedCalculations;
